import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../services/api';
import styles from './Auth.module.css';

const Register = ({ onSwitchToLogin, inviteCode = '' }) => {
  const [formData, setFormData] = useState({
    phone: '',
    code: '',
    password: '',
    confirmPassword: '',
    invite: inviteCode || '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [success, setSuccess] = useState(false);
  const [appliedInviteCode, setAppliedInviteCode] = useState(inviteCode);
  const { register } = useAuth();

  useEffect(() => {
    setAppliedInviteCode(inviteCode);
    if (inviteCode) {
      setFormData((prev) => ({
        ...prev,
        invite: inviteCode,
      }));
    }
  }, [inviteCode]);

  useEffect(() => {
    if (!countdown) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'invite' ? value.toUpperCase() : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
      general: '',
    }));

    if (name === 'invite') {
      setAppliedInviteCode(value.toUpperCase());
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const phone = formData.phone.trim();
    const code = formData.code.trim();

    if (!phone) {
      newErrors.phone = '请输入手机号';
    } else if (!/^\d{6,}$/.test(phone)) {
      newErrors.phone = '请输入正确的手机号';
    }

    if (!code) {
      newErrors.code = '请输入验证码';
    }

    if (!formData.password) {
      newErrors.password = '请输入密码';
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6个字符';
    } else if (formData.password.length > 50) {
      newErrors.password = '密码不能超过50个字符';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '请确认密码';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendCode = async () => {
    const phone = formData.phone.trim();
    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: '请输入手机号' }));
      return;
    }
    if (!/^\d{6,}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: '请输入正确的手机号' }));
      return;
    }
    setErrors((prev) => ({ ...prev, general: '', generalDetail: '' }));
    setSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/send_sms_code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || '验证码发送失败');
      }
      setCountdown(60);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        general: error.message || '验证码发送失败，请稍后重试',
      }));
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const phone = formData.phone.trim();
    const code = formData.code.trim();

    setLoading(true);
    setSuccess(false);

    try {
      const result = await register({
        phone,
        smsCode: code,
        username: phone, // 后端需要用户名，直接使用手机号
        password: formData.password,
        invite: formData.invite || undefined,
      });

      if (result.success) {
        setSuccess(true);
        setFormData({
          phone: '',
          code: '',
          password: '',
          confirmPassword: '',
          invite: inviteCode || '',
        });
        setCountdown(0);

        setTimeout(() => {
          onSwitchToLogin();
        }, 3000);
      } else {
        setErrors({ general: result.message || '注册失败' });
      }
    } catch (error) {
      setErrors({ general: '注册失败，请重试' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <div className={styles.loginVisual}>
            <div className={styles.visualBadge}>EnNovels</div>
            <h2 className={styles.visualTitle}>欢迎加入</h2>
            <p className={styles.visualSubtitle}>注册成功，开始阅读之旅</p>
          </div>
          <div className={styles.loginFormSection}>
            <div className={styles.formHeader}>
              <h1 className={styles.formTitle}>注册成功</h1>
              <p className={styles.formSubtitle}>我们将很快为你跳转到登录页</p>
            </div>
            <div className={styles.stepSection}>
              <p className={styles.subduedText}>您的账户已创建成功，3秒后自动跳转。</p>
              <button className={styles.loginBtn} onClick={onSwitchToLogin}>
                立即登录
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        <div className={styles.loginVisual}>
          <div className={styles.visualBadge}>EnNovels</div>
          <h2 className={styles.visualTitle}>加入我们</h2>
          <p className={styles.visualSubtitle}>手机号验证，安全创建账户</p>
        </div>

        <div className={styles.loginFormSection}>
          <div className={styles.formHeader}>
            <h1 className={styles.formTitle}>创建账户</h1>
            <p className={styles.formSubtitle}>手机号 + 验证码 + 密码，一步完成注册</p>
          </div>

          {errors.general && (
            <div className={`${styles.errorMessage} ${styles.generalError}`}>
              {errors.general}
            </div>
          )}

          <form className={styles.loginForm} onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label htmlFor="phone">手机号</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className={`${styles.inputField} ${errors.phone ? styles.inputError : ''}`}
                placeholder="请输入手机号"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading || sending}
              />
              {errors.phone && <div className={styles.errorMessage}>{errors.phone}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="code">短信验证码</label>
              <div className={styles.codeRow}>
                <input
                  type="text"
                  id="code"
                  name="code"
                  className={`${styles.inputField} ${errors.code ? styles.inputError : ''}`}
                  placeholder="请输入验证码"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.codeBtn}
                  onClick={handleSendCode}
                  disabled={sending || countdown > 0 || loading}
                >
                  {countdown > 0 ? `重新发送 (${countdown}s)` : '获取验证码'}
                </button>
              </div>
              {errors.code && <div className={styles.errorMessage}>{errors.code}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">密码</label>
              <div className={styles.inputWithIcon}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`${styles.inputField} ${styles.inputFieldWithIcon} ${errors.password ? styles.inputError : ''}`}
                  placeholder="请输入密码（至少6个字符）"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.eyeIcon}
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={loading}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  <span className="material-symbols-rounded">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {errors.password && <div className={styles.errorMessage}>{errors.password}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword">确认密码</label>
              <div className={styles.inputWithIcon}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`${styles.inputField} ${styles.inputFieldWithIcon} ${errors.confirmPassword ? styles.inputError : ''}`}
                  placeholder="请再次输入密码"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.eyeIcon}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  disabled={loading}
                  aria-label={showConfirmPassword ? '隐藏密码' : '显示密码'}
                >
                  <span className="material-symbols-rounded">
                    {showConfirmPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {errors.confirmPassword && (
                <div className={styles.errorMessage}>{errors.confirmPassword}</div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="invite">邀请码（可选）</label>
              <div className={styles.codeRow}>
                <input
                  type="text"
                  id="invite"
                  name="invite"
                  className={`${styles.inputField} ${styles.inviteInput}`}
                  placeholder="请输入邀请码"
                  value={formData.invite}
                  onChange={handleChange}
                  disabled={loading || Boolean(inviteCode)}
                />
                {inviteCode && <span className={styles.inviteBadge}>来自邀请链接</span>}
              </div>
              {appliedInviteCode && !inviteCode && (
                <div className={styles.formHint}>当前已使用邀请码：{appliedInviteCode}</div>
              )}
            </div>

            <button type="submit" className={styles.loginBtn} disabled={loading}>
              {loading ? '注册中...' : '注册'}
            </button>
          </form>

          <div className={styles.formFooter}>
            <div className={styles.switchText}>
              已有账户？
              <button
                type="button"
                className={styles.switchBtn}
                onClick={onSwitchToLogin}
                disabled={loading}
              >
                立即登录
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
